<template>
  <!-- eslint-disable vue/max-attributes-per-line -->
  <div>
    <!-- Breadcrumbs -->
    <v-breadcrumbs
      :items="breadcrumbs"
      divider="/"
      class="pa-0 mb-4"
    >
      <template #item="{ item }">
        <v-breadcrumbs-item
          :to="item.to"
          :disabled="item.disabled"
          exact
        >
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>

    <!-- Page title row -->
    <div class="d-flex align-center mb-5 flex-wrap" style="gap: 8px">
      <h1 class="text-2xl mr-2">
        Заявка #{{ requestId }}
      </h1>
      <benefit-status-chip v-if="requestItem" :status="requestItem.status" />
      <v-spacer />
      <v-btn
        icon
        :disabled="!requestItem"
        :aria-label="`Логи заявки #${requestId}`"
        @click="openLogsModal"
      >
        <v-icon>{{ mdiHistory }}</v-icon>
      </v-btn>
      <v-btn
        icon
        :aria-label="'Вернуться к списку заявок'"
        @click="goBack"
      >
        <v-icon>{{ mdiClose }}</v-icon>
      </v-btn>
    </div>

    <!-- Skeleton while loading -->
    <v-row v-if="loading">
      <v-col cols="12" md="5">
        <v-skeleton-loader type="card" />
      </v-col>
      <v-col cols="12" md="7">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <!-- Not found state -->
    <v-alert
      v-else-if="!requestItem && !loading"
      type="warning"
      outlined
    >
      Заявка не найдена или недоступна.
      <v-btn text small class="ml-2" @click="goBack">
        К списку
      </v-btn>
    </v-alert>

    <!-- Main layout: summary + decision -->
    <v-row v-else>
      <v-col cols="12" md="5">
        <benefit-request-summary-card :item="requestItem" />
      </v-col>
      <v-col cols="12" md="7">
        <benefit-decision-panel
          ref="decisionPanel"
          :item="requestItem"
          :category-items="categoryItems"
          :loading="actionLoading"
          :initial-category-id="requestItem ? requestItem.requested_category_id : ''"
          @approve="approveRequest"
          @reject="rejectRequest"
          @open-logs="openLogsModal"
        />
      </v-col>
    </v-row>

    <!-- Logs dialog -->
    <v-dialog
      v-model="logsDialog"
      max-width="960"
      scrollable
    >
      <v-card>
        <v-card-title>
          Логи заявки #{{ requestId }}
          <v-spacer />
          <v-btn icon aria-label="Закрыть" @click="logsDialog = false">
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <v-data-table
            :headers="logModalHeaders"
            :items="logs"
            :loading="logsLoading"
            dense
            hide-default-footer
          >
            <template #[`item.action_display`]="{ item }">
              <benefit-log-action-chip :action="item.action" />
            </template>
            <template #[`item.created_at`]="{ item }">
              {{ formatDateTime(item.created_at) }}
            </template>
            <template #[`item.log_subject`]="{ item }">
              {{ formatLogSubject(item) }}
            </template>
            <template #[`item.performed_by_name`]="{ item }">
              {{ formatLogActor(item) }}
            </template>
          </v-data-table>
          <div
            v-if="logsMeta.num_pages > 1"
            class="pt-4 d-flex align-center flex-wrap"
          >
            <span class="mr-4 text--secondary">Всего: {{ logsMeta.count }}</span>
            <v-pagination
              v-model="logsPage"
              :length="logsMeta.num_pages"
              :disabled="logsLoading"
              @input="fetchRequestLogs"
            />
          </div>
          <p
            v-else-if="logs.length && logsMeta.count"
            class="text--secondary pt-2 mb-0"
          >
            Записей: {{ logs.length }} из {{ logsMeta.count }}
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000">
      {{ snackbarText }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          OK
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiHistory, mdiClose } from '@mdi/js'
import Api from '@/api/api'
import {
  stripEmptyParams,
  formatDateTime,
  formatLogActor,
  formatLogSubject,
} from './utils'
import BenefitStatusChip from './components/BenefitStatusChip.vue'
import BenefitRequestSummaryCard from './components/BenefitRequestSummaryCard.vue'
import BenefitDecisionPanel from './components/BenefitDecisionPanel.vue'
import BenefitLogActionChip from './components/BenefitLogActionChip.vue'

export default {
  name: 'BenefitRequestProcess',
  components: {
    BenefitStatusChip,
    BenefitRequestSummaryCard,
    BenefitDecisionPanel,
    BenefitLogActionChip,
  },
  data() {
    return {
      mdiHistory,
      mdiClose,

      loading: false,
      actionLoading: false,
      requestItem: null,
      categoryItems: [],

      snackbar: false,
      snackbarText: '',
      snackbarColor: 'primary',

      logsDialog: false,
      logsLoading: false,
      logs: [],
      logsPage: 1,
      logsPageSize: 50,
      logsMeta: { count: 0, num_pages: 0, current_page: 1 },
      logModalHeaders: [
        { text: 'Время', value: 'created_at', width: 140 },
        { text: 'Действие', value: 'action_display', sortable: false },
        { text: 'Объект', value: 'log_subject', sortable: false },
        { text: 'Комментарий', value: 'comment' },
        { text: 'Кто', value: 'performed_by_name' },
      ],
    }
  },
  computed: {
    requestId() {
      return this.$route.params.id
    },
    breadcrumbs() {
      return [
        { text: 'Льготы', to: '/benefits/requests', disabled: false },
        { text: 'Заявки', to: '/benefits/requests', disabled: false },
        { text: `#${this.requestId}`, disabled: true },
      ]
    },
  },
  async created() {
    await this.loadCategories()
    await this.loadRequest()
  },
  methods: {
    formatDateTime,
    formatLogActor,
    formatLogSubject,

    toast(msg, color = 'primary') {
      this.snackbarText = msg
      this.snackbarColor = color
      this.snackbar = true
    },

    apiError(err, fallback = 'Ошибка запроса') {
      const d = err.response && err.response.data
      const msg = (d && (d.error || d.message)) || (d && d.data && d.data.error) || err.message || fallback
      this.toast(msg, 'error')
    },

    async loadCategories() {
      try {
        const res = await Api.getBenefitCategories()
        if (!res.success) {
          this.toast(res.error || 'Не удалось загрузить категории', 'error')
          return
        }
        this.categoryItems = res.data || []
      } catch (e) {
        this.apiError(e)
      }
    },

    async loadRequest() {
      this.loading = true
      try {
        const res = await Api.getBenefitRequests({
          request_id: this.requestId,
          page: 1,
          page_size: 1,
        })
        if (!res.success) {
          this.toast(res.error || 'Не удалось загрузить заявку', 'error')
          return
        }
        const requests = (res.data && res.data.requests) || []
        this.requestItem = requests.find(item => String(item.id) === String(this.requestId)) || null
        if (!this.requestItem) {
          this.toast('Заявка не найдена', 'error')
        }
      } catch (e) {
        this.apiError(e)
      } finally {
        this.loading = false
      }
    },

    async approveRequest(form) {
      if (!this.requestItem || this.actionLoading) return
      this.actionLoading = true
      try {
        const hasFile = form.document instanceof File
        let payload
        if (hasFile) {
          payload = new FormData()
          payload.append('request_id', String(this.requestItem.id))
          payload.append('category_id', String(form.category_id))
          if (form.active_from) payload.append('active_from', form.active_from)
          if (form.active_to) payload.append('active_to', form.active_to)
          if (form.comment) payload.append('comment', form.comment)
          payload.append('document', form.document)
        } else {
          payload = stripEmptyParams({
            request_id: this.requestItem.id,
            category_id: form.category_id,
            active_from: form.active_from || undefined,
            active_to: form.active_to || undefined,
            comment: form.comment || undefined,
          })
        }
        const res = await Api.approveBenefitRequest(payload)
        if (res.success === false) {
          this.toast(res.error || 'Ошибка одобрения', 'error')
          return
        }
        this.toast(res.message || 'Заявка одобрена, льгота создана', 'success')
        this.goBack()
      } catch (e) {
        this.apiError(e)
      } finally {
        this.actionLoading = false
      }
    },

    async rejectRequest(form) {
      if (!this.requestItem || this.actionLoading) return
      this.actionLoading = true
      try {
        const res = await Api.rejectBenefitRequest({
          request_id: this.requestItem.id,
          rejection_reason: form.rejection_reason,
        })
        if (res.success === false) {
          this.toast(res.error || 'Ошибка отклонения', 'error')
          return
        }
        this.toast(res.message || 'Заявка отклонена', 'success')
        this.goBack()
      } catch (e) {
        this.apiError(e)
      } finally {
        this.actionLoading = false
      }
    },

    openLogsModal() {
      if (!this.requestItem) return
      this.logsDialog = true
      this.logsPage = 1
      this.fetchRequestLogs()
    },

    async fetchRequestLogs() {
      if (!this.requestItem) return
      this.logsLoading = true
      try {
        const params = stripEmptyParams({
          request_id: this.requestItem.id,
          page: this.logsPage,
          page_size: this.logsPageSize,
        })
        const res = await Api.getBenefitLogs(params)
        if (!res.success) {
          this.toast(res.error || 'Не удалось загрузить логи', 'error')
          return
        }
        const data = res.data || {}
        this.logs = data.logs || []
        this.logsMeta = {
          count: data.count || 0,
          num_pages: data.num_pages || 0,
          current_page: data.current_page || this.logsPage,
        }
      } catch (e) {
        this.apiError(e)
      } finally {
        this.logsLoading = false
      }
    },

    goBack() {
      this.$router.push('/benefits/requests')
    },
  },
}
</script>
